import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, SectionLabel } from "@/components/motion";
import { SERVICES, IMAGES } from "@/lib/data";

const SERVICE_IMAGES = {
  flatbed: IMAGES.flatbed,
  project: IMAGES.heavyHaul,
  ltl: IMAGES.warehouse,
  recurring: IMAGES.cargoRacks,
};

export default function Services() {
  return (
    <main className="pt-16" data-testid="services-page">
      <section className="relative overflow-hidden border-b border-line">
        <img src={IMAGES.heavyHaul} alt="Heavy equipment hauling" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/70 to-asphalt/40" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8">
          <SectionLabel>// Our Freight Services</SectionLabel>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black uppercase leading-[1.05] tracking-tight text-bright sm:text-5xl lg:text-6xl">
            Every load. Every lane. <span className="text-hazard">Coordinated.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#9CA3AF] sm:text-lg">
            From single urgent hotshots to recurring contract lanes — dry van, flatbed, step deck, reefer and specialized equipment across Canada and U.S. cross-border markets.
          </p>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 2) * 0.08}>
              <div className="group relative flex h-full flex-col overflow-hidden border border-line bg-panel transition-colors duration-300 hover:bg-panel-hover" data-testid={`service-detail-${s.id}`}>
                {SERVICE_IMAGES[s.id] && (
                  <div className="relative h-44 overflow-hidden">
                    <img src={SERVICE_IMAGES[s.id]} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-panel to-transparent" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-7">
                  <div className="flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brass">{s.tag}</p>
                    <span className="font-mono text-xs text-dim">SVC-0{i + 1}</span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl font-bold uppercase text-bright transition-colors group-hover:text-hazard">{s.title}</h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#9CA3AF]">{s.desc}</p>
                  <Link to="/shippers" data-testid={`service-quote-${s.id}`} className="mt-6 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-hazard">
                    Quote this service <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <div className="border border-line bg-panel">
            <div className="hazard-stripes h-1.5 w-full" />
            <div className="flex flex-col items-start justify-between gap-4 p-8 sm:flex-row sm:items-center">
              <p className="font-display text-xl font-bold uppercase text-bright sm:text-2xl">
                Not sure which service fits? <span className="text-hazard">Send the spec — we'll route it.</span>
              </p>
              <Link to="/shippers" data-testid="services-cta-btn" className="shrink-0 bg-hazard px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-asphalt transition-colors hover:bg-hazard-dark">
                Request a Quote
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
